import React, { createContext, useContext, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import UseAuthConfig from "../hooks/useAuthConfig";
import { makeRequest } from "../utils/helpers/helperMethods/makeRequest";

export type OrganisationStatusState = {
  // False when the logged in user's organisation has been deactivated.
  isOrganisationActive: boolean;
  isLoading: boolean;
};

const defaultStatus: OrganisationStatusState = {
  isOrganisationActive: true,
  isLoading: true,
};

export const OrganisationStatusContext =
  createContext<OrganisationStatusState>(defaultStatus);

interface Props {
  children: React.ReactNode;
}

// Loads the logged in user's organisation status once per account.
export const OrganisationStatusProvider: React.FC<Props> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const { protectedResources }: any = UseAuthConfig();
  const [status, setStatus] = useState<OrganisationStatusState>(defaultStatus);
  const account = accounts[0];
  const accountId = account?.homeAccountId;

  useEffect(() => {
    if (inProgress !== InteractionStatus.None || !accountId) return;
    let isCancelled = false;

    instance
      .acquireTokenSilent({
        account,
        scopes: [
          ...protectedResources.apiTodoList.scopes.read,
          ...protectedResources.apiTodoList.scopes.write,
        ],
      })
      .then((tokenResponse) =>
        makeRequest(
          tokenResponse.accessToken,
          `${protectedResources.apiTodoList.endpoint}/users/current-user`,
          "GET",
          null,
          ""
        )
      )
      .then((response) => {
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        return response.json();
      })
      .then((user) => {
        if (isCancelled) return;
        setStatus({
          isOrganisationActive: user?.isOrganizationActive !== false,
          isLoading: false,
        });
      })
      .catch((error) => {
        // The API still rejects requests from deactivated organisations, so fail open here.
        console.error("Failed to load organisation status:", error);
        if (!isCancelled) setStatus({ isOrganisationActive: true, isLoading: false });
      });

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, inProgress]);

  return (
    <OrganisationStatusContext.Provider value={status}>
      {children}
    </OrganisationStatusContext.Provider>
  );
};

export const useOrganisationStatus = () => useContext(OrganisationStatusContext);
